import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { SerializedError } from "@reduxjs/toolkit";

export interface NormalizedApiResponse<T = any> {
    success: boolean;
    statusCode: number;
    message?: string;
    data?: T;
}

// START-CodeLeap [07-Aug-2026]: [1.0-R-Ops user & account list]
/**
 * Shapes an RTK Query trigger result (`await someMutation(...)`) into
 * normalizeAPIResponse's input. Pairs with normalizeAPIResponse.
 */
export const unwrapApiResult = <T>(
    result: { data: T } | { error: FetchBaseQueryError | SerializedError }
): {
    data?: T;
    error?: FetchBaseQueryError | SerializedError;
    isSuccess: boolean;
    isError: boolean;
} => ({
    data: "data" in result ? result.data : undefined,
    error: "error" in result ? result.error : undefined,
    isSuccess: "data" in result,
    isError: "error" in result,
});
// /END-CodeLeap [07-Aug-2026]: [1.0-R-Ops user & account list]

// START-CodeLeap [14-Aug-2026]: [#18188866946 PR-2469 review fixes]
/**
 * Pull a user-facing message off an RTK Query error. On a transport error the body is
 * on the error object (`error.data.message`), not on the (undefined) success payload.
 * Pass a resolved, translated `fallback` for when the body carries no message.
 */
export const getApiErrorMessage = (error: unknown, fallback: string): string =>
    (error as { data?: { message?: string } })?.data?.message ?? fallback;
// /END-CodeLeap [14-Aug-2026]: [#18188866946 PR-2469 review fixes]

// START-CodeLeap [27-Aug-2026]: [#18188866946 PR-2469 review fixes] one ISACCEPTFILE coercion
/**
 * Normalize the per-variable "accepts file input" flag coming off the API.
 * Params : raw (unknown) - ISACCEPTFILE / isAcceptFile as the API sent it
 * Returns: boolean
 *
 * MySQL stores the flag as 1/0 and the CF layer can hand it back as a number, a numeric
 * string, or absent. Number() covers all three; a bare Boolean() would not - Boolean("0")
 * is true, which flipped the flag ON for variables that reject files. Every read site uses
 * this so the four inline variants that had already diverged cannot drift again.
 */
export const isAcceptFileFlag = (raw: unknown): boolean => Number(raw ?? 0) === 1;

/**
 * Inverse of isAcceptFileFlag, for the write direction.
 * Params : value (boolean)
 * Returns: 0 | 1 - the shape the ISACCEPTFILE column expects
 */
export const toDbFlag = (value: boolean): 0 | 1 => (value ? 1 : 0);
// /END-CodeLeap [27-Aug-2026]: [#18188866946 PR-2469 review fixes]

/**
 * Normalizes RTK Query hook results into a single shape.
 * When neither `isError` nor `isSuccess` is true (e.g. loading or uninitialized), returns `message: "Unknown state"`.
 */
export const normalizeAPIResponse = <T = any>(
    result: {
        data?: any;
        error?: FetchBaseQueryError | SerializedError;
        isSuccess: boolean;
        isError: boolean;
    }
): NormalizedApiResponse<T> => {

    if (result.isError) {
        const error = result.error;

        let statusCode = 500;
        let message = "Something went wrong. Please try again.";
        let errorData: unknown = undefined;

        if (error && "status" in error) {
            const fetchErr = error as FetchBaseQueryError;
            statusCode = typeof fetchErr.status === "number" ? fetchErr.status : 500;

            if (typeof fetchErr.data === "object" && fetchErr.data !== null && "message" in fetchErr.data) {
                message = (fetchErr.data as { message?: string }).message || message;
            } else if (typeof fetchErr.data === "string") {
                message = fetchErr.data;
            }
            errorData = fetchErr.data;
        } else if (error && "message" in error) {
            message = error.message || message;
        }

        return {
            success: false,
            statusCode,
            message,
            data: errorData as T | undefined
        };
    }

    if (result.isSuccess) {
        const actualData = result.data;
        const hasBody = actualData != null;
        const httpStatus = actualData?._meta?.httpStatus ?? actualData?.statusCode ?? 200;

        return {
            success: (actualData.success === true || httpStatus === 200  ) ? true : false,
            statusCode: httpStatus,
            message: actualData?.message,
            data: hasBody ? (actualData as T) : undefined
        };
    }

    return {
        success: false,
        statusCode: 500,
        message: "Unknown state"
    };
};
